import { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Loader2, AlertCircle } from "lucide-react";

export default function ChangePasswordModal() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState("");
  const [npwd, setNpwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const forced = !!user?.must_change_password;
  if (!forced) return null;

  const strength = (p) => {
    let n = 0;
    if (p.length >= 8) n++;
    if (p.length >= 12) n++;
    if (/[A-Z]/.test(p) && /[a-z]/.test(p)) n++;
    if (/\d/.test(p)) n++;
    if (/[^A-Za-z0-9]/.test(p)) n++;
    return n; // 0..5
  };
  const sc = strength(npwd);
  const scLabel = ["Très faible", "Faible", "Correct", "Bon", "Fort", "Excellent"][sc];
  const scColor = ["#EF4444", "#F59E0B", "#F59E0B", "#10B981", "#10B981", "#059669"][sc];

  const submit = async (e) => {
    e.preventDefault();
    if (npwd !== confirm) { toast.error("Les mots de passe ne correspondent pas"); return; }
    if (sc < 3) { toast.error("Mot de passe trop faible (min. correct)"); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: npwd });
      toast.success("Mot de passe mis à jour");
      await refresh?.();
      // Force reload to clear must_change_password state
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Changement impossible");
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={true}>
      <DialogContent className="max-w-md" onEscapeKeyDown={(e) => e.preventDefault()} onPointerDownOutside={(e) => e.preventDefault()} data-testid="change-password-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[#C5A059]" />
            Première connexion — Changement obligatoire
          </DialogTitle>
        </DialogHeader>
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 flex items-start gap-2 mb-2">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>Pour sécuriser votre espace, choisissez un nouveau mot de passe personnel. Vous ne pourrez pas accéder à l'application tant que ce n'est pas fait.</div>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Mot de passe actuel (temporaire reçu par email)</Label>
            <div className="relative mt-1">
              <Input type={show ? "text" : "password"} value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="cp-current" autoFocus required />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-2 text-slate-400 hover:text-slate-700">{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
            </div>
          </div>
          <div>
            <Label>Nouveau mot de passe</Label>
            <Input type={show ? "text" : "password"} value={npwd} onChange={(e) => setNpwd(e.target.value)} data-testid="cp-new" required className="mt-1" minLength={8} />
            {npwd && (
              <div className="mt-1">
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${(sc/5)*100}%`, backgroundColor: scColor }} />
                </div>
                <div className="text-[10px] mt-0.5" style={{ color: scColor }}>{scLabel}</div>
              </div>
            )}
          </div>
          <div>
            <Label>Confirmer</Label>
            <Input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" required className="mt-1" />
            {confirm && confirm !== npwd && <div className="text-[10px] text-red-600 mt-1">Les mots de passe ne correspondent pas</div>}
          </div>
          <Button type="submit" disabled={submitting || sc < 3 || npwd !== confirm || !current} data-testid="cp-submit" className="w-full bg-[#C5A059] hover:bg-[#A88846] text-white">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Valider
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
